import { useState } from 'react';
import {
  AccountModel,
  DiaChi,
  DonViCoSo,
  HanThanhToan,
  KhachHang,
  NgoaiTe,
  NhaCungCap,
  NhanVien,
  PhuongThucGiaoHang,
  PhuongTienVanChuyen,
  QuyenChungTu
} from '@/types/schemas';

export interface FormFieldState {
  nhaCungCap: KhachHang | null;
  nhanVien: NhanVien | null;
  taiKhoan: AccountModel | null;
  hanThanhToan: HanThanhToan | null;
  chietKhau: number | null;

  quyenChungTu: QuyenChungTu | null;
  ngoaiTe: NgoaiTe | null;
  donViCoSo: DonViCoSo | null;

  diaChi: DiaChi | null;
  phuongTienVanChuyen: PhuongTienVanChuyen | null;
  phuongTienGiaoHang: PhuongThucGiaoHang | null;
  cucThue: KhachHang | null;

  soChungTu: string;

  ma_gd: string;
  ma_gd_code: string;
  px_yn: boolean;
  pc_tao_yn: boolean;
  ck_yn: boolean;
  loai_ck: string;
  ck_tl_nt: number;

  ma_httt: string;

  ngay_ct: string;
}

export interface FormFieldActions {
  setNhaCungCap: (nhaCungCap: KhachHang) => void;
  setNhanVien: (nhanVien: NhanVien) => void;
  setTaiKhoan: (taiKhoan: AccountModel | null) => void;
  setHanThanhToan: (hanThanhToan: HanThanhToan) => void;
  setQuyenChungTu: (quyenChungTu: QuyenChungTu) => void;
  setNgoaiTe: (ngoaiTe: NgoaiTe) => void;

  setSoChungTu: (soChungTu: string) => void;

  setHdbhYn: (value: boolean) => void;
  setPxYn: (value: boolean) => void;
  setPtTaoYn: (value: boolean) => void;
  setCkYn: (value: boolean) => void;
  setLoaiCk: (value: string) => void;
  setCkTlNt: (value: number) => void;
  setMaGd: (value: string, ma_gd_code?: string) => void;

  setMaHttt: (value: string) => void;
  setNgayCt: (value: string) => void;

  resetState: () => void;
  updateState: (updates: Partial<FormFieldState>) => void;
}

export interface UseFormFieldStateReturn {
  state: FormFieldState;
  actions: FormFieldActions;
}

const initialState: FormFieldState = {
  nhaCungCap: null,
  nhanVien: null,
  taiKhoan: null,
  hanThanhToan: null,
  chietKhau: 0,

  quyenChungTu: null,
  ngoaiTe: null,
  donViCoSo: null,

  diaChi: null,
  phuongTienVanChuyen: null,
  phuongTienGiaoHang: null,
  cucThue: null,

  soChungTu: '',

  ma_gd: '',
  ma_gd_code: '',
  px_yn: true,
  pc_tao_yn: false,
  ck_yn: false,
  loai_ck: '2',
  ck_tl_nt: 0,

  ma_httt: 'TMM',

  ngay_ct: new Date().toISOString().split('T')[0]
};

function transformInitialData(initialData?: MuaHangRecord): Partial<FormFieldState> {
  if (!initialData) return {};

  return {
    nhaCungCap: initialData.ma_kh_data || null,
    nhanVien: initialData.ma_nv_data || initialData.ma_nvmh_data || null,
    taiKhoan: initialData.tk_data || null,
    hanThanhToan: initialData.han_tt_data || null,

    quyenChungTu: initialData.ma_nk_data || null,
    ngoaiTe: initialData.ma_nt_data || null,

    soChungTu: initialData.so_ct || '',

    ma_gd: initialData.ma_gd ?? 'DV',
    ma_gd_code: initialData.ma_gd_data?.ma_gd || '',
    px_yn: initialData.px_yn ?? true,
    pc_tao_yn: initialData.pc_tao_yn ?? false,
    ck_yn: initialData.ck_yn ?? false,
    loai_ck: initialData.loai_ck || '2',
    ck_tl_nt: initialData.ck_tl_nt || 0,

    ma_httt: initialData.ma_httt || 'TMM',

    ngay_ct: initialData.ngay_ct || new Date().toISOString().split('T')[0]
  };
}

export function useFormFieldState(initialData?: MuaHangRecord): UseFormFieldStateReturn {
  const [state, setState] = useState<FormFieldState>({
    ...initialState,
    ...transformInitialData(initialData)
  });

  const actions: FormFieldActions = {
    setNhaCungCap: (nhaCungCap: KhachHang) => {
      setState(prev => ({
        ...prev,
        nhaCungCap
      }));
    },

    setCkTlNt: (ck_tl_nt: number) => {
      setState(prev => ({
        ...prev,
        ck_tl_nt
      }));
    },

    setNhanVien: (nhanVien: NhanVien) => {
      setState(prev => ({
        ...prev,
        nhanVien
      }));
    },

    setTaiKhoan: (taiKhoan: AccountModel | null) => {
      setState(prev => ({
        ...prev,
        taiKhoan
      }));
    },

    setHanThanhToan: (hanThanhToan: HanThanhToan) => {
      setState(prev => ({
        ...prev,
        hanThanhToan
      }));
    },

    setQuyenChungTu: (quyenChungTu: QuyenChungTu) => {
      setState(prev => ({
        ...prev,
        quyenChungTu
      }));
    },

    setNgoaiTe: (ngoaiTe: NgoaiTe) => {
      setState(prev => ({
        ...prev,
        ngoaiTe
      }));
    },

    setMaHttt: (value: string) => {
      setState(prev => ({ ...prev, ma_httt: value }));
    },

    setNgayCt: (value: string) => {
      setState(prev => ({ ...prev, ngay_ct: value }));
    },

    setMaGd: (value: string, ma_gd_code?: string) => {
      setState(prev => ({ ...prev, ma_gd: value, ...(ma_gd_code !== undefined ? { ma_gd_code } : {}) }));
    },

    setSoChungTu: (soChungTu: string) => {
      setState(prev => ({ ...prev, soChungTu }));
    },

    setHdbhYn: (value: boolean) => {
      setState(prev => ({ ...prev, hdbh_yn: value }));
    },

    setPxYn: (value: boolean) => {
      setState(prev => ({ ...prev, px_yn: value }));
    },

    setPtTaoYn: (value: boolean) => {
      setState(prev => ({ ...prev, pc_tao_yn: value }));
    },

    setCkYn: (value: boolean) => {
      setState(prev => ({ ...prev, ck_yn: value }));
    },

    setLoaiCk: (value: string) => {
      setState(prev => ({ ...prev, loai_ck: value }));
    },

    resetState: () => {
      setState(initialState);
    },

    updateState: (updates: Partial<FormFieldState>) => {
      setState(prev => ({ ...prev, ...updates }));
    }
  };

  return { state, actions };
}
